import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getSharedLinkPublic } from '@/entities/shared-link/api/get-shared-link';
import {
  SharedLinkLoading,
  SharedLinkUnavailable,
} from '@/entities/shared-link/ui';
import type { ApiError } from '@/error-handler/ApiError';

import type { SharedLinkError } from '../types';
import { SharedLinkPasswordForm } from '../ui';

const SharedLinkPage = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<SharedLinkError | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);

  const handleVerify = async (passwordToUse?: string) => {
    if (!linkId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getSharedLinkPublic({
        linkId,
        password: passwordToUse,
      });

      // Redirect to the actual content
      if (response.link) {
        window.location.href = response.link;
      }
    } catch (err) {
      const axiosError = err as ApiError;
      const errorCode = axiosError.data?.client_code as SharedLinkError;

      if (errorCode === 'shared_link__password_empty') {
        setNeedsPassword(true);
        setError(null);
      } else if (errorCode === 'shared_link__password_wrong') {
        setError('shared_link__password_wrong');
      } else {
        // All other errors show unavailable page
        setError(errorCode || 'unknown');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = (password: string) => {
    handleVerify(password);
  };

  // Initial load
  useEffect(() => {
    handleVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkId]);

  // Loading state
  if (isLoading && !needsPassword) {
    return <SharedLinkLoading />;
  }

  // Password required state
  if (needsPassword) {
    return (
      <SharedLinkPasswordForm
        isLoading={isLoading}
        hasError={error === 'shared_link__password_wrong'}
        onSubmit={handlePasswordSubmit}
      />
    );
  }

  // Link unavailable state (expired, not found, etc.)
  if (error) {
    return <SharedLinkUnavailable />;
  }

  // Fallback loading
  return <SharedLinkLoading />;
};

export default SharedLinkPage;
